import { useNavigate, useParams } from "@tanstack/solid-router";
import { createMutation, createQuery } from "@tanstack/solid-query";
import { For, Show, createEffect, createSignal } from "solid-js";
import { track } from "~/lib/analytics";
import { AvatarUpload } from "~/components/AvatarUpload";
import { WizardStepper } from "~/components/WizardStepper";
import {
  ApiError,
  claimTicket,
  fetchInterests,
  fetchMe,
  fetchProfile,
  isProfileOnboardingComplete,
  parseClaimError,
  updateProfile,
  type ClaimSupportCTA,
  type CommunityProfile,
} from "~/lib/api";
import { supportMailtoFromCTA } from "~/lib/support";
import { cityLabel, ticketPurchaseUrlForEvent, TICKET_PURCHASE_LABEL } from "~/lib/tickets";

type WizardStep = "claim" | "profile" | "interests" | "optin";

export function OnboardingPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const eventId = () => params().eventId ?? "";

  const [step, setStep] = createSignal<WizardStep>("claim");
  const [draftMode, setDraftMode] = createSignal(false);
  const [claimSupport, setClaimSupport] = createSignal<ClaimSupportCTA | null>(null);
  const [displayName, setDisplayName] = createSignal("");
  const [headline, setHeadline] = createSignal("");
  const [bio, setBio] = createSignal("");
  const [company, setCompany] = createSignal("");
  const [jobRole, setJobRole] = createSignal("");
  const [selectedInterests, setSelectedInterests] = createSignal<string[]>([]);
  const [optIn, setOptIn] = createSignal(false);
  const [avatarUrl, setAvatarUrl] = createSignal<string | null>(null);

  createEffect(() => {
    const current = step();
    if (!eventId()) return;
    track("onboarding_step_viewed", { step: current }, { ticketingEventId: eventId() });
  });

  const meQuery = createQuery(() => ({ queryKey: ["me"], queryFn: fetchMe }));

  const profileQuery = createQuery(() => ({
    queryKey: ["profile", eventId()],
    queryFn: () => fetchProfile(eventId()),
    enabled: !!eventId(),
  }));

  const interestsQuery = createQuery(() => ({
    queryKey: ["interests"],
    queryFn: fetchInterests,
    enabled: step() === "interests" || step() === "optin",
  }));

  const claimMutation = createMutation(() => ({
    mutationFn: () => claimTicket(eventId()),
    onSuccess: (res) => {
      setClaimSupport(null);
      seedFromProfile(res.data);
      profileQuery.refetch();
      setStep("profile");
    },
    onError: (err: unknown) => {
      const parsed = parseClaimError(err);
      if (parsed?.support) {
        setClaimSupport(parsed.support);
        return;
      }
      setClaimSupport({
        email: "info@dataengbytes.com",
        title: "Could not claim ticket",
        body: err instanceof ApiError ? err.message : "Something went wrong. Try again or contact support.",
      });
    },
  }));

  const saveProfileMutation = createMutation(() => ({
    mutationFn: () =>
      updateProfile({
        ticketing_event_id: eventId(),
        display_name: displayName().trim(),
        headline: headline().trim() || undefined,
        bio: bio().trim() || undefined,
        company: company().trim() || undefined,
        job_role: jobRole().trim() || undefined,
      }),
    onSuccess: () => {
      profileQuery.refetch();
      setStep("interests");
    },
  }));

  const saveInterestsMutation = createMutation(() => ({
    mutationFn: () =>
      updateProfile({
        ticketing_event_id: eventId(),
        interest_tag_ids: selectedInterests(),
      }),
    onSuccess: () => {
      profileQuery.refetch();
      setStep("optin");
    },
  }));

  const saveDraftMutation = createMutation(() => ({
    mutationFn: () =>
      updateProfile({
        ticketing_event_id: eventId(),
        interest_tag_ids: selectedInterests(),
        display_name: displayName().trim(),
        headline: headline().trim() || undefined,
        bio: bio().trim() || undefined,
        company: company().trim() || undefined,
        job_role: jobRole().trim() || undefined,
      }),
    onSuccess: () => {
      navigate({ to: "/directory/$eventId/edit", params: { eventId: eventId() } });
    },
  }));

  const finishMutation = createMutation(() => ({
    mutationFn: () =>
      updateProfile({
        ticketing_event_id: eventId(),
        networking_opt_in: true,
        visibility: "directory",
        interest_tag_ids: selectedInterests(),
        display_name: displayName().trim(),
        headline: headline().trim() || undefined,
        bio: bio().trim() || undefined,
        company: company().trim() || undefined,
        job_role: jobRole().trim() || undefined,
      }),
    onSuccess: () => {
      navigate({ to: "/directory/$eventId", params: { eventId: eventId() } });
    },
  }));

  function seedFromProfile(p: CommunityProfile | null) {
    if (!p) return;
    if (p.display_name) setDisplayName(p.display_name);
    if (p.headline) setHeadline(p.headline);
    if (p.bio) setBio(p.bio);
    if (p.company) setCompany(p.company);
    if (p.job_role) setJobRole(p.job_role);
    if (p.interest_tag_ids?.length) setSelectedInterests([...p.interest_tag_ids]);
    setOptIn(p.networking_opt_in);
  }

  createEffect(() => {
    const id = eventId();
    if (!id) {
      navigate({ to: "/" });
      return;
    }
    const data = profileQuery.data?.data;
    if (data && isProfileOnboardingComplete(data)) {
      navigate({ to: "/directory/$eventId", params: { eventId: id } });
      return;
    }
    if (data?.attendee_id) {
      seedFromProfile(data);
      if (step() === "claim" && !claimMutation.isPending) {
        if (data.interest_tag_ids?.length) {
          setStep("optin");
        } else if (data.headline || data.bio) {
          setStep("interests");
        } else {
          setStep("profile");
        }
      }
    } else if (
      step() === "claim" &&
      !draftMode() &&
      !claimMutation.isPending &&
      !claimSupport() &&
      !claimMutation.isError &&
      !claimMutation.isSuccess
    ) {
      claimMutation.mutate();
    }
  });

  function startDraftProfile() {
    setDraftMode(true);
    setClaimSupport(null);
    const data = profileQuery.data?.data;
    if (data) seedFromProfile(data);
    if (data?.interest_tag_ids?.length) {
      setStep("interests");
    } else if (data?.display_name) {
      setStep("profile");
    } else {
      setStep("profile");
    }
  }

  function toggleInterest(id: string) {
    setSelectedInterests((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 10 ? [...prev, id] : prev,
    );
  }

  const eventLabel = () => cityLabel(eventId());
  const purchaseUrl = () => ticketPurchaseUrlForEvent(eventId());

  return (
    <section class="card wizard">
      <p class="status-chip">Setup · {eventLabel()}</p>
      <Show when={step() !== "claim"}>
        <WizardStepper current={step()} draft={draftMode()} />
      </Show>

      <Show when={!eventId()}>
        <p class="muted">No event selected.</p>
      </Show>

      <Show when={step() === "claim"}>
        <h1>Link your ticket</h1>
        <p class="muted">We match your sign-in email to your {eventLabel()} ticket automatically.</p>

        <Show when={claimMutation.isPending}>
          <p class="muted">Checking your ticket…</p>
        </Show>

        <Show when={claimSupport()} keyed>
          {(support) => (
            <div class="support-box">
              <h2>{support.title}</h2>
              <p>{support.body}</p>
              <p>
                <a class="btn btn-primary" href={supportMailtoFromCTA(support, meQuery.data?.data.email, eventLabel())}>
                  Email {support.email}
                </a>
              </p>
              <p class="muted">Include:</p>
              <ul class="support-list">
                <li>The email you used to sign in to Connect</li>
                <li>The email on your ticket (or your name and company as on the ticket)</li>
                <li>Which conference: {eventLabel()}</li>
              </ul>
              <p class="claim-gate-foot muted">
                Don't have a ticket yet? Purchase a conference ticket at{" "}
                <a href={purchaseUrl()} target="_blank" rel="noopener noreferrer">
                  {TICKET_PURCHASE_LABEL}
                </a>
                . See <a href="/help#claim">how to claim</a> or the <a href="/help">FAQ</a>.
              </p>
              <div class="wizard-actions">
                <button type="button" class="btn btn-ghost" onClick={() => navigate({ to: "/" })}>
                  Choose another city
                </button>
                <a
                  class="btn btn-outline"
                  href={purchaseUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Purchase a ticket
                </a>
                <button
                  type="button"
                  class="btn btn-ghost"
                  onClick={startDraftProfile}
                >
                  Build a draft profile
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  onClick={() => {
                    setClaimSupport(null);
                    claimMutation.mutate();
                  }}
                >
                  Try again
                </button>
              </div>
            </div>
          )}
        </Show>
      </Show>

      <Show when={step() === "profile"}>
        <h1>Your profile</h1>
        <Show
          when={draftMode()}
          fallback={
            <p class="muted">Ticket linked. Add how you'd like to appear in the directory.</p>
          }
        >
          <p class="muted">
            Draft profile — save your details now. Link your ticket later to appear in the
            directory.
          </p>
        </Show>

        <AvatarUpload
          eventId={eventId()}
          displayName={displayName()}
          avatarUrl={avatarUrl() ?? profileQuery.data?.data?.avatar_url}
          onUploaded={(url) => setAvatarUrl(url)}
        />

        <form
          class="wizard-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!displayName().trim()) return;
            saveProfileMutation.mutate();
          }}
        >
          <label class="field">
            <span>Display name</span>
            <input
              required
              value={displayName()}
              onInput={(e) => setDisplayName(e.currentTarget.value)}
              placeholder="Alex Engineer"
            />
          </label>
          <label class="field">
            <span>Headline</span>
            <input
              value={headline()}
              onInput={(e) => setHeadline(e.currentTarget.value)}
              placeholder="Building real-time pipelines"
            />
          </label>
          <label class="field">
            <span>Company</span>
            <input
              value={company()}
              onInput={(e) => setCompany(e.currentTarget.value)}
              placeholder="Acme Data"
            />
          </label>
          <label class="field">
            <span>Role</span>
            <input
              value={jobRole()}
              onInput={(e) => setJobRole(e.currentTarget.value)}
              placeholder="Data Engineer"
            />
          </label>
          <label class="field">
            <span>Bio</span>
            <textarea
              rows={4}
              value={bio()}
              onInput={(e) => setBio(e.currentTarget.value)}
              placeholder="What you work on and what you'd like to talk about"
            />
          </label>

          <Show when={saveProfileMutation.isError}>
            <div class="error-box">{(saveProfileMutation.error as Error).message}</div>
          </Show>

          <div class="wizard-actions">
            <button type="submit" class="btn btn-primary" disabled={saveProfileMutation.isPending}>
              Continue
            </button>
          </div>
        </form>
      </Show>

      <Show when={step() === "interests"}>
        <h1>Your interests</h1>
        <p class="muted">Pick topics so others can find you (up to 10).</p>

        <Show when={interestsQuery.isLoading}>
          <p class="muted">Loading interests…</p>
        </Show>
        <Show when={interestsQuery.isError}>
          <div class="error-box">{(interestsQuery.error as Error).message}</div>
        </Show>
        <Show when={interestsQuery.data}>
          <div class="interest-grid">
            <For each={interestsQuery.data!.data}>
              {(tag) => (
                <button
                  type="button"
                  class={`interest-chip${selectedInterests().includes(tag.id) ? " selected" : ""}`}
                  onClick={() => toggleInterest(tag.id)}
                >
                  {tag.label}
                </button>
              )}
            </For>
          </div>
          <p class="muted">{selectedInterests().length} of 10 selected</p>
        </Show>

        <Show when={saveInterestsMutation.isError}>
          <div class="error-box">{(saveInterestsMutation.error as Error).message}</div>
        </Show>

        <div class="wizard-actions">
          <button type="button" class="btn btn-ghost" onClick={() => setStep("profile")}>
            Back
          </button>
          <Show
            when={draftMode()}
            fallback={
              <button
                type="button"
                class="btn btn-primary"
                disabled={selectedInterests().length === 0 || saveInterestsMutation.isPending}
                onClick={() => saveInterestsMutation.mutate()}
              >
                Continue
              </button>
            }
          >
            <button
              type="button"
              class="btn btn-primary"
              disabled={saveDraftMutation.isPending}
              onClick={() => saveDraftMutation.mutate()}
            >
              Save draft profile
            </button>
          </Show>
        </div>
        <Show when={saveDraftMutation.isError}>
          <div class="error-box">{(saveDraftMutation.error as Error).message}</div>
        </Show>
      </Show>

      <Show when={step() === "optin" && !draftMode()}>
        <h1>Join the directory</h1>
        <p class="muted">Preview how you'll appear to other attendees.</p>

        <div class="profile-preview-card">
          <div class="profile-preview-avatar" aria-hidden="true">
            {(displayName() || "?").charAt(0).toUpperCase()}
          </div>
          <div class="profile-preview-body">
            <strong>{displayName()}</strong>
            <span class="profile-preview-headline">{headline() || jobRole() || "Attendee"}</span>
            <span class="muted">{company()}</span>
            <Show when={bio()}>
              <p class="profile-preview-bio">{bio()}</p>
            </Show>
          </div>
        </div>

        <label class="optin-row">
          <input
            type="checkbox"
            checked={optIn()}
            onChange={(e) => setOptIn(e.currentTarget.checked)}
          />
          <span>Show my profile in the {eventLabel()} attendee directory</span>
        </label>

        <Show when={finishMutation.isError}>
          <div class="error-box">{(finishMutation.error as Error).message}</div>
        </Show>

        <div class="wizard-actions">
          <button type="button" class="btn btn-ghost" onClick={() => setStep("interests")}>
            Back
          </button>
          <button
            type="button"
            class="btn btn-primary"
            disabled={!optIn() || finishMutation.isPending}
            onClick={() => finishMutation.mutate()}
          >
            Go to directory
          </button>
        </div>
      </Show>
    </section>
  );
}